import React, { InputHTMLAttributes, TextareaHTMLAttributes, forwardRef, useEffect, useState } from 'react';

import Icon from 'components/common/Icons/base/Icon/Icon';
import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';

import styles from './TextInput.module.scss';

export interface BaseInputProps {
    /**
     * The label for the input
     */
    label?: string;
    /**
     * Helper text to display below the input
     */
    helperText?: string;
    /**
     * If true, the input will indicate an error
     */
    error?: boolean;
    /**
     * If true, the input will be disabled
     */
    disabled?: boolean;
    /**
     * If true, the input will be required
     */
    required?: boolean;
    /**
     * Icon to display at the start of the input
     */
    startIcon?: React.ReactNode;
    /**
     * Icon to display at the end of the input
     */
    endIcon?: React.ReactNode;
    /**
     * Custom class name
     */
    className?: string;
    /**
     * If true, creates a textarea instead of input
     */
    multiline?: boolean;
    /**
     * Number of rows for textarea
     */
    rows?: number;
    /**
     * If true, shows a clear button when input has value
     */
    clearable?: boolean;
    /**
     * If true, shows a search button when input has value
     */
    searchable?: boolean;
    /**
     * Callback when clear button is clicked
     */
    onClear?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    /**
     * Callback when search button is clicked
     */
    onSearch?: () => void;
}

export type TextInputProps = BaseInputProps &
    (
        | (Omit<InputHTMLAttributes<HTMLInputElement>, keyof BaseInputProps> & { multiline?: false })
        | (Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, keyof BaseInputProps> & {
              multiline: true;
          })
    );

/**
 * Custom TextInput component built from scratch
 * Implements design system styling from Figma atoms
 * Supports light and dark themes via CSS variables
 */
export const TextInput = forwardRef<HTMLInputElement | HTMLTextAreaElement, TextInputProps>(
    (
        {
            label,
            helperText,
            error = false,
            disabled = false,
            required = false,
            startIcon,
            endIcon,
            className = '',
            multiline = false,
            rows = 3,
            id,
            clearable = true,
            onClear,
            searchable = false,
            onSearch,
            ...props
        },
        ref,
    ) => {
        const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
        const [hasValue, setHasValue] = useState(false);

        // Track whether the input has a value
        useEffect(() => {
            const value = (props as any).value || (props as any).defaultValue;
            setHasValue(!!value && value.length > 0);
        }, [(props as any).value]);

        const handleClear = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            if (disabled) return;

            // Call onClear if provided
            if (onClear) {
                onClear(e);
            }

            // Call onChange with empty value if provided
            if ((props as any).onChange) {
                const event = {
                    target: { value: '' },
                } as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
                (props as any).onChange(event);
            }

            // Clear the input directly if using ref
            if (ref && typeof ref !== 'function') {
                if (ref.current) {
                    (ref.current as HTMLInputElement | HTMLTextAreaElement).value = '';
                }
            }

            setHasValue(false);
        };

        const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setHasValue(!!e.target.value && e.target.value.length > 0);
            if ((props as any).onChange) {
                (props as any).onChange(e);
            }
        };

        const showClearButton = clearable && hasValue && !disabled;
        const showSearchButton = searchable && !disabled;

        const containerClass = [
            styles.container,
            error ? styles.error : '',
            disabled ? styles.disabled : '',
            startIcon ? styles.withStartIcon : '',
            endIcon || showClearButton ? styles.withEndIcon : '',
            className,
        ]
            .filter(Boolean)
            .join(' ');

        return (
            <div className={containerClass}>
                {label && (
                    <label htmlFor={inputId} className={styles.label}>
                        {label}
                        {required && <span className={styles.required}>*</span>}
                    </label>
                )}

                <div className={styles.wrapper}>
                    {startIcon && <div className={styles.iconStart}>{startIcon}</div>}

                    {multiline ? (
                        <textarea
                            ref={ref as React.Ref<HTMLTextAreaElement>}
                            id={inputId}
                            className={styles.field}
                            disabled={disabled}
                            required={required}
                            rows={rows}
                            {...(props as TextareaHTMLAttributes<HTMLTextAreaElement>)}
                            onChange={clearable ? handleChange : (props as any).onChange}
                        />
                    ) : (
                        <input
                            ref={ref as React.Ref<HTMLInputElement>}
                            id={inputId}
                            className={styles.field}
                            disabled={disabled}
                            required={required}
                            {...(props as InputHTMLAttributes<HTMLInputElement>)}
                            onChange={clearable ? handleChange : (props as any).onChange}
                        />
                    )}

                    {showSearchButton && (
                        <div className={`${styles.iconEnd} ${styles.clearIcon}`} onClick={onSearch}>
                            <MuiIcon icon={'search'} fontSize={'2rem'} color={'var(--text-primary)'} type={'round'} />
                        </div>
                    )}

                    {showClearButton && (
                        <div className={`${styles.iconEnd} ${styles.clearIcon}`} onClick={handleClear}>
                            <MuiIcon icon={'close'} fontSize={'1.8rem'} color={'var(--text-primary)'} type={'round'} />
                        </div>
                    )}

                    {endIcon && <div className={styles.iconEnd}>{endIcon}</div>}
                </div>

                {helperText && <div className={styles.helperText}>{helperText}</div>}
            </div>
        );
    },
);

export default TextInput;
